import { useEffect, useRef, useState } from 'react';
import type { ActiveFilter } from '../../../types/filters';
import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';
import { useTabPaneContext } from '../routing/TabPaneContext';
import { loadPanePersistedFilters } from './paneFilterStorage';

export {
  buildPaneFilterSessionKey,
  clearPanePersistedFiltersForPath,
  getPaneFilterStorageKey,
  loadPanePersistedFilters,
  PANE_FILTERS_STORAGE_PREFIX,
  savePanePersistedFilters,
} from './paneFilterStorage';

/**
 * Aplica filtros default só na primeira carga quando não há filtros salvos
 * nem ativos. Persistência entre abas é feita por `useDynamicFilters`.
 *
 * @returns `filtersInitialized` — true quando defaults/persistência já foram
 * resolvidos e a carga de dados pode prosseguir.
 */
export function usePanePersistedActiveFilters(
  activeFilters: ActiveFilter[],
  setActiveFilters: React.Dispatch<React.SetStateAction<ActiveFilter[]>>,
  defaultFilters: ActiveFilter[],
  ready: boolean,
  scope = 'default',
): boolean {
  const paneContext = useTabPaneContext();
  const panePath = paneContext?.panePath;
  const persistEnabled = isTabKeepAliveEnabled() && !!panePath;
  const initializedRef = useRef(false);
  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  useEffect(() => {
    if (!ready) {
      setFiltersInitialized(false);
      return;
    }

    if (initializedRef.current) {
      return;
    }

    if (persistEnabled && panePath) {
      const stored = loadPanePersistedFilters(panePath, scope);
      if (stored !== null) {
        initializedRef.current = true;
        setFiltersInitialized(true);
        return;
      }
    }

    if (activeFiltersRef.current.length > 0) {
      initializedRef.current = true;
      setFiltersInitialized(true);
      return;
    }

    if (defaultFilters.length > 0) {
      setActiveFilters(defaultFilters);
      initializedRef.current = true;
      setFiltersInitialized(true);
      return;
    }

    // Metadados prontos, porém sem defaults configurados (ex.: erro parcial).
    initializedRef.current = true;
    setFiltersInitialized(true);
  }, [ready, defaultFilters, panePath, persistEnabled, scope, setActiveFilters]);

  return filtersInitialized;
}
